import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LumivetaLogo from '../../components/LumivetaLogo';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, user } = useAuth();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const pollTimeoutRef = useRef(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (sessionId && !hasProcessedRef.current) {
      hasProcessedRef.current = true;
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus('error');
    }
    
    // Cleanup on unmount
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 15; // Increased attempts
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      console.log(`[PaymentSuccess] Checking payment status, attempt ${attempts + 1}/${maxAttempts}`);
      const data = await api.getPaymentStatus(sessionId);
      
      if (data.payment_status === 'paid') {
        console.log('[PaymentSuccess] Payment confirmed! Updating user...');
        setStatus('success');
        
        // CRITICAL: Refresh user data to get updated subscription status
        // This ensures canAccessApp is updated before redirect
        await refreshUser();
        
        // Give a moment for state to propagate
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh again to ensure we have the latest data
        const updatedUser = await refreshUser();
        console.log('[PaymentSuccess] User refreshed:', updatedUser?.subscription_status);
        
        // AUTO-REDIRECT to Dashboard after showing success
        pollTimeoutRef.current = setTimeout(() => {
          console.log('[PaymentSuccess] Redirecting to dashboard...');
          navigate('/main-dashboard', { replace: true });
        }, 2500);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      pollTimeoutRef.current = setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('[PaymentSuccess] Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      pollTimeoutRef.current = setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const handleGoToDashboard = async () => {
    // Refresh user one more time before navigating
    await refreshUser();
    navigate('/main-dashboard', { replace: true });
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center">
            <div className="w-20 h-20 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
              Processing Payment...
            </h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your subscription
            </p>
            <p className="text-xs text-muted-foreground mt-4">
              Attempt {attempts + 1} of 15
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6 gold-glow">
              <Icon name="Crown" size={40} color="#FFD700" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-gold-gradient mb-2">
              Welcome to Premium!
            </h2>
            <p className="text-muted-foreground mb-6">
              Your subscription is now active. Enjoy your spiritual guidance!
            </p>
            <div className="glass rounded-xl p-4 mb-6">
              <h3 className="font-medium text-foreground mb-2">Your Benefits:</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} color="#10B981" />
                  7 daily reflections
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} color="#10B981" />
                  Full 7-part scripture pipeline
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} color="#10B981" />
                  Journaling & Field Notes
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={16} color="#10B981" />
                  Priority support
                </li>
              </ul>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Redirecting to Dashboard...</p>
            <Button
              variant="default"
              fullWidth
              onClick={handleGoToDashboard}
              className="gold-glow"
            >
              Start Your Journey Now
            </Button>
          </div>
        );

      case 'timeout':
      case 'error':
      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-warning/20 flex items-center justify-center mx-auto mb-6">
              <Icon name="AlertCircle" size={40} color="#FFD700" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
              {status === 'expired' ? 'Session Expired' : 'Verification Pending'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment immediately. If you were charged, your subscription will be activated shortly.'
              }
            </p>
            <div className="space-y-3">
              <Button
                variant="default"
                fullWidth
                onClick={handleGoToDashboard}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => navigate('/subscription-plans')}
              >
                View Plans
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Payment Status - Lumiveta</title>
      </Helmet>

      <div className="min-h-dvh bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <LumivetaLogo size="large" className="justify-center" />
          </div>

          <div className="glass rounded-2xl p-8 border border-primary/20">
            {renderContent()}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            © 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™
          </p>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccess;
